/* eslint-disable no-alert */
import { useRef, useEffect, useState } from 'react';
import useGlobal from 'hooks/useGlobal';
import fetchPayment from 'api/payment';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import useCustomerAccessToken from 'hooks/useCustomerAccessToken';
import checkoutIdMutation from '@/queries/setCheckoutId';
import customerAssociatedCheckoutMutation from '@/queries/setCustomerAssociatedCheckout';
import useCustomer from '@/hooks/useCustomer';

export default function usePaymentRef({ emitGlobal, ...props }) {
  const { customerAccessToken } = useCustomerAccessToken();
  const { refetchCustomer, customer } = useCustomer();
  const { state } = useGlobal();
  const [setCheckoutId] = useMutation(checkoutIdMutation);
  const [setCustomerAssociatedCheckout] = useMutation(
    customerAssociatedCheckoutMutation
  );
  const client = useApolloClient();
  const [active, setActive] = useState('');
  const [ready, setReady] = useState(false);
  const billingAddress = useRef();
  const paymentForm = useRef();

  const setRef = () => {
    paymentForm.current = new window.SqPaymentForm({
      applicationId: process.env.GATSBY_SQUARE_APPID,
      inputClass: 'sq-input',
      autoBuild: false,
      // Customize the CSS for SqPaymentForm iframe elements
      inputStyles: [
        {
          fontSize: '16px',
          lineHeight: '40px',
          color: '#132f30',
          padding: ' 0',
          placeholderColor: '#cbcbcb',
          backgroundColor: '#ffffff',
          fontFamily: 'arial narrow'
        }
      ],
      // Initialize the credit card placeholders
      cardNumber: {
        elementId: 'sq-card-number',
        placeholder: 'Card Number'
      },
      cvv: {
        elementId: 'sq-cvv',
        placeholder: 'CVV'
      },
      expirationDate: {
        elementId: 'sq-expiration-date',
        placeholder: 'MM/YY'
      },
      postalCode: {
        elementId: 'sq-postal-code',
        placeholder: 'Postal'
      },
      // SqPaymentForm callback functions
      callbacks: {
        paymentFormLoaded() {
          setReady(true);
        },
        inputEventReceived(inputEvent) {
          // console.log(inputEvent);
          switch (inputEvent.eventType) {
            case 'focusClassAdded':
              setActive(inputEvent.field);
              break;
            case 'focusClassRemoved':
              setActive('');
              break;
            case 'cardBrandChanged':
              // set card brand here if we want
              break;
            case 'postalCodeChanged':
              break;
            default:
              break;
          }
        },
        async cardNonceResponseReceived(errors, nonce) {
          if (errors) {
            console.log(errors, 'error');
            emitGlobal.update({
              paymentProcessing: false,
              paymentSuccess: false,
              paymentErrors: errors.map(({ message }) => message)[0],
            });
            // Log errors from nonce generation to the browser developer console.
            console.error('Encountered errors:');
            errors.forEach(error => {
              console.error(`  ${error.message}`);
            });
            // alert(
            //   'Encountered errors, check browser developer console for more details'
            // );
            return;
          }
          try {
            const data = await fetchPayment({
              nonce,
              ...props,
              billingAddress: {
                ...billingAddress.current
              },
              customerAccessToken,
              email: customer.email
            });
            if (data.orderUrl) {
              await setCheckoutId({
                variables: {
                  id: ''
                }
              });
              await setCustomerAssociatedCheckout({
                variables: {
                  associatedCheckout: ''
                }
              });
              await client.writeData({
                data: {
                  attributes: []
                }
              });
              await refetchCustomer();
              await new Promise(resolve => {
                setTimeout(() => {
                  resolve(true);
                }, 500);
              });
              window.location.href = data.orderUrl;
            } else {
              emitGlobal.update({
                paymentProcessing: false,
                paymentSuccess: true
              });
            }
          } catch (error) {
            emitGlobal.update({
              paymentProcessing: false,
              paymentSuccess: false,
              paymentErrors: ['Payment Failed. Please check your card details.']
            });
            console.log(error.title, error.result);
            console.log(error.message);
          }
        }
      }
    });
  };

  useEffect(() => {
    if (state?.formReady) {
      setRef();
    }
  }, [state?.formReady]);

  useEffect(() => {
    billingAddress.current = props.billingAddress;
  }, [props.billingAddress]);

  useEffect(() => {
    if (ready) {
      paymentForm.current.setPostalCode(props.billingAddress.zip);
    }
  }, [ready, props.billingAddress.zip]);

  return { paymentForm, active, ready };
}
